import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import Yup from '~yup';

import LabelledInput from '~components/atoms/LabelledInput';
import ActionButton from '~components/atoms/ActionButton';

import { DiscountStatusEnum, DiscountUsageTypeEnum, TransactionActionEnum } from '~utils/enums';
import UtilsFunctions from '~utils/functions';

import * as Styles from './_productManagementDiscountDetailsForm.module.scss';
import Text from '~components/atoms/Text';
import LabelledDropdown from '~components/atoms/LabelledDropdown';
import LabelledDatepicker from '~components/atoms/LabelledDatepicker';
import dayjs from 'dayjs';
import { TDiscountDetailsSettings } from '~types/feat/setting';
import combinedActions from '~redux/combinedActions';
import { useAppDispatch } from '~redux/hooks';

const ProductSettingDetailsFormValidation = Yup.object().shape({
    discountCode: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchZeroWhiteSpace().required('Discount Code is required'),
    discountName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().required('Discount Name is required'),
    amount: Yup.number().typeError('Value must be a number').required('Amount is required'),
    discountType: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(Object.values(TransactionActionEnum).filter(e => Number.isNaN(Number(e))) as string[]).label('Discount Type').required('Discount Type is required'),
    usageType: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(Object.values(DiscountUsageTypeEnum).filter(e => Number.isNaN(Number(e))) as string[]).label('Discount Usage Type').required('Discount Usage Type is required'),
    status: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(Object.values(DiscountStatusEnum).filter(e => Number.isNaN(Number(e))) as string[]).label('Status').required('Status is required'),
    discountStartDate: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().label('Discount Start Date'),
    discountEndDate: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().label('Discount End Date'),
    discountAmountType: Yup.boolean().required('Select an ammount type'),
});

type TProductManagementDiscountDeleteForm = {
    discountDetails?: TDiscountDetailsSettings;
    submitButtonLabel: string;
    isEditing?: boolean;
    inputIsDisabled?: boolean;
    actionButtonClassName?: string;
    actionButtonIsLoading?: boolean;
}

const DiscountDetailsForm = ( prop: TProductManagementDiscountDeleteForm) => {
    const dispatch = useAppDispatch();

    const {
        discountDetails,
        submitButtonLabel,
        isEditing,
        inputIsDisabled,
        actionButtonClassName,
        actionButtonIsLoading,
    } = prop;

    const {
        Misc:
        {
            blockEnterKey,
        },
    } = UtilsFunctions;

    const statusTypeOptions = Object.values(DiscountStatusEnum).filter(e => Number.isNaN(Number(e))).map(key => {
        return {
            label: key as string,
            value: key as string,
        };
    });

    const discountTypeOptions = Object.values(TransactionActionEnum).filter(e => Number.isNaN(Number(e))).map(key => {
        return {
            label: key as string,
            value: key as string,
        };
    });

    const discountUsageTypeOptions = Object.values(DiscountUsageTypeEnum).filter(e => Number.isNaN(Number(e))).map(key => {
        return {
            label: key as string,
            value: key as string,
        };
    });

    const getAmount = (fixedValue: number | undefined, percentageValue: number | undefined) => {
        if (fixedValue){
            const shifted = ((fixedValue * 100) / 100);

            if (Number.isInteger(shifted * 100)) {
                return shifted.toFixed(2);
            }

            return parseFloat(shifted.toFixed(6)).toString();
        }

        if (percentageValue){
            const shifted = percentageValue * 100;

            if (Number.isInteger(shifted * 100)) {
                return shifted.toFixed(2);
            }

            return parseFloat(shifted.toFixed(6)).toString();
        }

        return '';
    };

    const ProductSettingDetailsFormInitialValues = {
        discountCode: discountDetails?.discountCode || '',
        discountName: discountDetails?.discountName || '',
        amount: getAmount(discountDetails?.amount, discountDetails?.percentageAmount),
        discountType: discountDetails?.transactionType ? TransactionActionEnum[discountDetails.transactionType] : '',
        usageType: discountDetails?.usageType ? DiscountUsageTypeEnum[discountDetails.usageType] : '',
        status: discountDetails?.status ? DiscountStatusEnum[discountDetails.status] : '',
        discountStartDate: discountDetails?.discountStartDate || '',
        discountEndDate: discountDetails?.discountEndDate || '',
        discountAmountType: !!discountDetails?.amount,
    };

    const formik = useFormik({
        initialValues: ProductSettingDetailsFormInitialValues,
        validationSchema: ProductSettingDetailsFormValidation,
        onSubmit: (values) => {
            const {
                discountCode,
                discountName,
                amount,
                discountType,
                discountAmountType,
                status,
                usageType,
                discountStartDate,
                discountEndDate,
            } = values;

            dispatch(combinedActions.settingUpdateDiscountAttempt({
                discountId: discountDetails?.discountId || 0,
                discountCode,
                discountName,
                amount: discountAmountType ? Number(amount) : undefined,
                percentageAmount: !discountAmountType ? Number(amount) : undefined,
                transactionType: TransactionActionEnum[discountType as keyof typeof TransactionActionEnum],
                status: submitButtonLabel === 'Delete' ? DiscountStatusEnum.Deactivated : DiscountStatusEnum[status as keyof typeof DiscountStatusEnum],
                usageType: DiscountUsageTypeEnum[usageType as keyof typeof DiscountUsageTypeEnum],
                discountStartDate,
                discountEndDate,
            }));
        },
    });

    useEffect(() => {
        if (!isEditing) {
            formik.resetForm();
        }
    }, [isEditing]);

    const renderDiscountDetails = () => {
        return (
            <form
                onSubmit={formik.handleSubmit}
                className={Styles['form-container']}
                onKeyDown={blockEnterKey}
            >
                <div className={Styles['form-subcontainer']}>
                    <LabelledInput
                        name='discountCode'
                        label='Discount Code: '
                        value={formik.values.discountCode || ''}
                        error={formik.touched.discountCode ? formik.errors.discountCode : undefined}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={Styles['label-no-margin']}
                        disabled={inputIsDisabled || !isEditing}
                    />

                    <LabelledInput
                        name='discountName'
                        label='Discount Name: '
                        value={formik.values.discountName || ''}
                        error={formik.touched.discountName ? formik.errors.discountName : undefined}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={Styles['label-no-margin']}
                        disabled={inputIsDisabled || !isEditing}
                    />

                    <div >
                        <Text
                            isBold
                            label='Amount: '
                            className={Styles.label}
                        />

                        <div className={Styles['flex-span-container']}>
                            <LabelledInput
                                name='amount'
                                label=''
                                value={formik.values.amount || ''}
                                error={formik.touched.amount ? formik.errors.amount : undefined}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={Styles['label-no-margin']}
                                disabled={inputIsDisabled || !isEditing}
                            />

                            <div className={Styles['flex-div']}>
                                <div>
                                    <input
                                        type='radio'
                                        id='html'
                                        name='discountAmountType'
                                        value='HTML'
                                        checked={formik.values.discountAmountType}
                                        onChange={(e) => formik.setFieldValue('discountAmountType', true)}
                                    />
                                    <label htmlFor='html'>Fixed (RM)</label><br/>
                                </div>

                                <div>
                                    <input
                                        type='radio'
                                        id='css'
                                        name='discountAmountType'
                                        value='CSS'
                                        checked={!formik.values.discountAmountType}
                                        onChange={(e) => formik.setFieldValue('discountAmountType', false)}
                                    />
                                    <label htmlFor='css'>Percentage (%)</label><br/>
                                </div>
                            </div>
                        </div>
                    </div>

                    <LabelledDropdown
                        id='discountType'
                        name='discountType'
                        label='Discount Type* :'
                        options={discountTypeOptions}
                        value={formik.values.discountType || ''}
                        error={formik.touched.discountType ? formik.errors.discountType : undefined}
                        onChange={(e) => formik.setFieldValue('discountType', e.target.value)}
                        onBlur={formik.handleBlur}
                        autoComplete='off'
                        disabled={inputIsDisabled || !isEditing}
                        noOptionsMessage='No results found'
                        optionSelected={!!TransactionActionEnum[formik.values.discountType as keyof typeof TransactionActionEnum]}
                    />

                    <LabelledDropdown
                        id='status'
                        name='status'
                        label='Status* :'
                        options={statusTypeOptions}
                        value={formik.values.status || ''}
                        error={formik.touched.status ? formik.errors.status : undefined}
                        onChange={(e) => formik.setFieldValue('status', e.target.value)}
                        onBlur={formik.handleBlur}
                        autoComplete='off'
                        disabled={inputIsDisabled || !isEditing}
                        noOptionsMessage='No results found'
                        optionSelected={!!DiscountStatusEnum[formik.values.status as keyof typeof DiscountStatusEnum]}
                    />

                    <LabelledDropdown
                        id='usageType'
                        name='usageType'
                        label='Discount Usage Type* :'
                        options={discountUsageTypeOptions}
                        value={formik.values.usageType || ''}
                        error={formik.touched.usageType ? formik.errors.usageType : undefined}
                        onChange={(e) => formik.setFieldValue('usageType', e.target.value)}
                        onBlur={formik.handleBlur}
                        autoComplete='off'
                        disabled={inputIsDisabled || !isEditing}
                        noOptionsMessage='No results found'
                        optionSelected={!!DiscountUsageTypeEnum[formik.values.usageType as keyof typeof DiscountUsageTypeEnum]}
                    />

                    {/* <div></div> */}

                    <LabelledDatepicker
                        id='discountStartDate'
                        name='discountStartDate'
                        label='Discount Start Date:'
                        selectTime
                        selectedDate={formik.values.discountStartDate}
                        error={formik.touched.discountStartDate ? formik.errors.discountStartDate : undefined}
                        setSelectedDate={(date: string) => formik.setFieldValue('discountStartDate', dayjs(date).toISOString())}
                        onBlur={formik.handleBlur}
                        disabled={inputIsDisabled || !isEditing}
                        minDate={dayjs().toISOString()}
                    />

                    <LabelledDatepicker
                        id='discountEndDate'
                        name='discountEndDate'
                        label='Discount End Date:'
                        selectTime
                        selectedDate={formik.values.discountEndDate}
                        error={formik.touched.discountEndDate ? formik.errors.discountEndDate : undefined}
                        setSelectedDate={(date: string) => formik.setFieldValue('discountEndDate', dayjs(date).toISOString())}
                        onBlur={formik.handleBlur}
                        disabled={inputIsDisabled || !isEditing}
                        minDate={dayjs().toISOString()}
                    />
                </div>

                <div className={Styles.container}>
                    <ActionButton
                        label={submitButtonLabel}
                        type='submit'
                        className={`${actionButtonClassName} ${actionButtonIsLoading ? Styles['action-button-disabled'] : ''}`}
                        disabled={submitButtonLabel === 'Delete' ? false : !isEditing || !formik.dirty}
                    />
                </div>
            </form>
        );
    };

    return (
        <>
            {renderDiscountDetails()}
        </>
    );
};

export default DiscountDetailsForm;
